import { useCallback, useRef } from 'react'
import { formatNumber } from '../../common/utils'
import { ZoomWindow } from '../../utils/compareView'

/**
 * Links a set of CanvasJS charts so they act as one time axis.
 *
 * - Zoom/pan/reset (rangeChanged) is reported upward as a ZoomWindow; the
 *   caller keeps it in state and feeds it back as every chart's axisX viewport,
 *   so all charts zoom together.
 * - Hover is driven by the mouse's time, not by any chart's data: every chart
 *   gets a crosshair at that exact time and its own tooltip, which reads
 *   "No data" when the series has no reading near it. CanvasJS's own tooltip
 *   snaps to the nearest point (a series that starts on Oct 5 would pull the
 *   line to Oct 5), so charts using this hook disable it and render this one.
 *   All of it is imperative on the chart instances, so hovering never
 *   re-renders React.
 */

// Readings further than this many typical intervals from the cursor count as a gap
const GAP_INTERVALS = 3
const MIN_GAP_MS = 30 * 60 * 1000

interface Entry {
  chart: any
  overlay: HTMLDivElement
  cleanup: () => void
}

// Typical spacing per dataPoints array, cached (arrays are rebuilt when data changes)
const toleranceCache = new WeakMap<any[], number>()

export function gapTolerance(points: { x: number }[]): number {
  const cached = toleranceCache.get(points)
  if (cached !== undefined) return cached
  const gaps: number[] = []
  for (let i = 1; i < points.length; i++) gaps.push(points[i].x - points[i - 1].x)
  gaps.sort((a, b) => a - b)
  const median = gaps.length ? gaps[Math.floor(gaps.length / 2)] : 0
  const tolerance = Math.max(MIN_GAP_MS, median * GAP_INTERVALS)
  toleranceCache.set(points, tolerance)
  return tolerance
}

// Nearest point to x in an x-ascending array, or null if it is past the gap tolerance
export function nearestPoint(points: any[], x: number): any | null {
  if (!points?.length) return null
  let lo = 0
  let hi = points.length - 1
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if (points[mid].x < x) lo = mid + 1
    else hi = mid
  }
  const after = points[lo]
  const before = lo > 0 ? points[lo - 1] : null
  const best = before && Math.abs(before.x - x) <= Math.abs(after.x - x) ? before : after
  return Math.abs(best.x - x) <= gapTolerance(points) ? best : null
}

function createOverlay(): HTMLDivElement {
  const el = document.createElement('div')
  el.setAttribute('aria-hidden', 'true')
  Object.assign(el.style, {
    position: 'absolute',
    display: 'none',
    pointerEvents: 'none',
    zIndex: '10',
    whiteSpace: 'nowrap',
    background: 'white',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '13px',
    boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
  })
  return el
}

// One line per series: its value at x, or "No data". Built with textContent:
// series names include user-entered device names.
function fillOverlay(el: HTMLDivElement, x: number, series: any[]) {
  el.replaceChildren()
  const date = document.createElement('div')
  date.style.color = '#666'
  date.textContent = new Date(x).toLocaleString()
  el.appendChild(date)

  for (const s of series) {
    const line = document.createElement('div')
    const name = document.createElement('span')
    name.style.color = s.color
    name.textContent = `${s.name}: `
    line.appendChild(name)

    const point = nearestPoint(s.dataPoints, x)
    if (point) {
      const value = document.createElement('strong')
      const real = point.realY !== undefined ? point.realY : point.y
      value.textContent = `${formatNumber(real)}${point.unit ? ` ${point.unit}` : ''}`
      line.appendChild(value)
    } else {
      const none = document.createElement('em')
      none.style.color = '#888'
      none.textContent = 'No data'
      line.appendChild(none)
    }
    el.appendChild(line)
  }
}

export function useSyncedCharts(onZoomChange: (zoom: ZoomWindow | null) => void) {
  const entries = useRef(new Map<string, Entry>())
  const refCallbacks = useRef(new Map<string, (chart: any) => void>())
  const onZoomRef = useRef(onZoomChange)
  onZoomRef.current = onZoomChange

  // `source` is the chart under the mouse. Its crosshair is left to CanvasJS:
  // crosshair.showAt/hide clear the overlay canvas, which is also where the
  // drag-to-zoom selection shading is drawn, so touching it erases the shading.
  const hideAll = useCallback((source?: any) => {
    entries.current.forEach(({ chart, overlay }) => {
      overlay.style.display = 'none'
      if (chart === source) return
      try {
        chart.axisX?.[0]?.crosshair?.hide?.()
      } catch {
        // chart mid-teardown
      }
    })
  }, [])

  const showAll = useCallback((x: number, source: any) => {
    entries.current.forEach(({ chart, overlay }) => {
      // Per chart, so one that isn't rendered yet or is mid-teardown (a pending
      // frame after a series moved) can't stop the rest from updating
      try {
        const axis = chart.axisX?.[0]
        if (!axis || x < axis.viewportMinimum || x > axis.viewportMaximum) {
          overlay.style.display = 'none'
          return
        }
        if (chart !== source) axis.crosshair?.showAt?.(x)
        fillOverlay(overlay, x, chart.options?.data ?? [])

        const px = axis.convertValueToPixel(x)
        const width = chart.width ?? overlay.parentElement?.clientWidth ?? 0
        const onRight = px < width / 2
        overlay.style.left = `${onRight ? px + 12 : px - 12}px`
        overlay.style.top = `${(chart.plotArea?.y1 ?? 0) + 8}px`
        overlay.style.transform = onRight ? 'none' : 'translateX(-100%)'
        overlay.style.display = 'block'
      } catch {
        overlay.style.display = 'none'
      }
    })
  }, [])

  // Stable per-id ref callback for CanvasJSChart's onRef
  const register = useCallback(
    (id: string) => {
      let cb = refCallbacks.current.get(id)
      if (!cb) {
        cb = (chart: any) => {
          const existing = entries.current.get(id)
          if (existing) {
            existing.cleanup()
            entries.current.delete(id)
          }
          if (!chart) {
            refCallbacks.current.delete(id)
            return
          }

          const container: HTMLElement | undefined = chart.container
          if (!container) return
          const overlay = createOverlay()
          container.appendChild(overlay)

          let frame = 0
          const onMove = (e: MouseEvent) => {
            const clientX = e.clientX
            cancelAnimationFrame(frame)
            frame = requestAnimationFrame(() => {
              const rect = container.getBoundingClientRect()
              const px = clientX - rect.left
              const area = chart.plotArea
              if (!area || px < area.x1 || px > area.x2) {
                hideAll(chart)
                return
              }
              showAll(chart.axisX[0].convertPixelToValue(px), chart)
            })
          }
          // Leaves this chart's own crosshair alone, so a drag-zoom that slips
          // past the edge keeps its selection shading
          const onLeave = () => {
            cancelAnimationFrame(frame)
            hideAll(chart)
          }
          container.addEventListener('mousemove', onMove)
          container.addEventListener('mouseleave', onLeave)

          entries.current.set(id, {
            chart,
            overlay,
            cleanup: () => {
              cancelAnimationFrame(frame)
              container.removeEventListener('mousemove', onMove)
              container.removeEventListener('mouseleave', onLeave)
              overlay.remove()
            },
          })
        }
        refCallbacks.current.set(id, cb)
      }
      return cb
    },
    [hideAll, showAll]
  )

  const rangeChanged = useCallback((e: any) => {
    if (e?.trigger === 'reset') {
      onZoomRef.current(null)
      return
    }
    const axis = e?.axisX?.[0]
    const min = Number(axis?.viewportMinimum)
    const max = Number(axis?.viewportMaximum)
    if (Number.isFinite(min) && Number.isFinite(max) && max > min) {
      onZoomRef.current({ min, max })
    }
  }, [])

  return { register, rangeChanged }
}
